/**
 * WorkflowSessionsTable — renders the sortable sessions table and pagination.
 */

import { Typography } from "@altimateai/lego";
import type { WorkflowSession, SessionStatus } from "./types";
import styles from "./workflow.module.scss";

// ── Types ──────────────────────────────────────────────────────────────────

export type SortField = "created_at" | "title" | "status" | "channel" | "priority";
export type SortOption = "recent" | "oldest" | "priority";

const statusStyleMap: Record<SessionStatus, string> = {
  active: "online",
  completed: "completed",
  archived: "offline",
  cancelled: "error",
};

const formatDate = (iso: string) => {
  return new Date(iso).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isActiveStatus = (status: SessionStatus) => status === "active";

const getRowStatusClass = (status: SessionStatus): string => {
  switch (statusStyleMap[status]) {
    case "online":
      return styles.rowBorderOnline;
    case "completed":
      return styles.rowBorderCompleted;
    case "error":
      return styles.rowBorderError;
    default:
      return styles.rowBorderOffline;
  }
};

// ── Props ──────────────────────────────────────────────────────────────────

interface PaginationInfo {
  page: number;
  totalPages: number;
  hasPrev: boolean;
  hasNext: boolean;
}

interface Props {
  sessions: WorkflowSession[];
  allSessions: WorkflowSession[];
  sortOption: SortOption;
  getSortIcon: (field: SortField) => string;
  onSort: (field: SortField) => void;
  onViewSession: (sessionId: string) => void;
  pagination?: PaginationInfo;
  onPageChange: (updater: (prev: number) => number) => void;
}

// ── Component ──────────────────────────────────────────────────────────────

export const WorkflowSessionsTable = ({
  sessions,
  allSessions,
  getSortIcon,
  onSort,
  onViewSession,
  pagination,
  onPageChange,
}: Props) => {
  return (
    <>
      <div className={styles.sessionsTable}>
        <div className={styles.tableHeader}>
          <button type="button" className={styles.columnHeader} onClick={() => onSort("status")}>
            Status {getSortIcon("status")}
          </button>
          <button type="button" className={styles.columnHeader} onClick={() => onSort("title")}>
            Title {getSortIcon("title")}
          </button>
          <button type="button" className={styles.columnHeader} onClick={() => onSort("priority")}>
            Priority {getSortIcon("priority")}
          </button>
          <button type="button" className={styles.columnHeader} onClick={() => onSort("channel")}>
            Channel {getSortIcon("channel")}
          </button>
          <button type="button" className={styles.columnHeader} onClick={() => onSort("created_at")}>
            Created {getSortIcon("created_at")}
          </button>
          <div className={styles.columnHeader}>Agent</div>
          <div className={styles.columnHeader}>Actions</div>
        </div>

        <div className={styles.tableBody}>
          {sessions.map(session => (
            <div
              key={session.id}
              className={`${styles.sessionRow} ${
                isActiveStatus(session.status) ? styles.activeRow : ""
              } ${getRowStatusClass(session.status)}`}
              role="button"
              tabIndex={0}
              onClick={() => onViewSession(session.id)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") onViewSession(session.id);
              }}
            >
              <div className={styles.statusCell}>
                <span className={`${styles.statusBadge} ${styles[statusStyleMap[session.status]]}`}>
                  {session.status.replace(/_/g, " ")}
                </span>
                {isActiveStatus(session.status) && <span className={styles.liveBadge}>LIVE</span>}
              </div>

              <div className={styles.agentCell}>
                <div className={styles.agentName} title={session.title ?? "(untitled)"}>
                  {session.title ?? "(untitled)"}
                </div>
                <div className={styles.sessionId} title={session.id}>
                  {session.id.slice(0, 8)}...
                </div>
              </div>

              <div className={styles.channelCell}>
                <span className={styles.skillTag} style={{ fontSize: "0.75rem" }}>
                  {session.priority ?? "medium"}
                </span>
              </div>

              <div className={styles.channelCell} title={session.channel ?? "-"}>
                {session.channel ?? "-"}
              </div>

              <div className={styles.timeCell}>
                {session.created_at && (
                  <div className={styles.startTime}>{formatDate(session.created_at)}</div>
                )}
              </div>

              <div className={styles.channelCell} title={session.current_agent_id ?? "-"}>
                {session.current_agent_id
                  ? session.current_agent_id.slice(0, 8) + "..."
                  : "-"}
              </div>

              <div className={styles.actionsCell}>
                <div className={styles.actionButtons}>
                  <button
                    type="button"
                    className={`${styles.actionBtn} ${styles.viewBtn}`}
                    onClick={e => {
                      e.stopPropagation();
                      onViewSession(session.id);
                    }}
                    title="View session"
                  >
                    View
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {sessions.length === 0 && allSessions.length > 0 && (
          <div className={styles.noResults}>
            <Typography variant="body">No sessions found matching your filters.</Typography>
          </div>
        )}
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className={styles.paginationBar}>
          <button
            type="button"
            className={styles.paginationBtn}
            disabled={!pagination.hasPrev}
            onClick={() => onPageChange(p => p - 1)}
          >
            Previous
          </button>
          <span className={styles.paginationInfo}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            type="button"
            className={styles.paginationBtn}
            disabled={!pagination.hasNext}
            onClick={() => onPageChange(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
};
